import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material';
import { RecordService } from '../../../../services/record.service';
import { AddMaterialComponent } from '../add-material/add-material.component';
import { filter, switchMap } from 'rxjs/operators';
import { Job } from '../../../../objects/job';
import { NotificationService } from '../../../../services/notification.service';
import { MaterialLineItem } from './material-line-item';
import { LooseObject } from '../../../../objects/loose-object';
import { get, isEmpty, isUndefined, filter as _filter, map as _map } from 'lodash-es';
import { toFormattedNumber } from '../../../../shared/utils/numbers';
import { BehaviorSubject, forkJoin, iif, of, Subscription } from 'rxjs';
import {
  arr_count,
  arr_first,
  blank,
  data_is_false,
  data_is_true,
  either,
  filled,
  observe,
} from '../../../../shared/utils/common';
import {
  DefaultMaterialJob,
  DefaultMaterialPricebook,
  DefaultMaterialTask,
  MaterialLineData,
  OnMaterialsUpdate,
} from './components/material-lines/material-lines.component';
import { ApiService } from '../../../../services/api.service';

@Component({
  selector: 'log-materials',
  templateUrl: './log-materials.component.html',
  styleUrls: ['./log-materials.component.scss'],
})
export class LogMaterialsComponent implements OnInit, OnDestroy {
  /**
   * Will contain the list of line items
   * to be logged as materials.
   *
   * @var {MaterialLineItem[]}
   */
  public arMaterialLineItem: MaterialLineItem[] = [];

  /**
   * Is the entire form touched.
   *
   * @var {boolean}
   */
  public bIsTouched: boolean = false;

  /**
   * Is the save api still loading?
   *
   * @var {boolean}
   */
  public bIsLoading: boolean = false;

  /**
   * Is the list still loading?
   *
   * @var {boolean}
   */
  readonly fetching$ = new BehaviorSubject<boolean>(false);

  arDeletedLineItems: LooseObject[] = [];

  readonly pricebookId$ = new BehaviorSubject<string|null>(null);

  readonly defaultPricebook$ = new BehaviorSubject<DefaultMaterialPricebook|null>(null);

  readonly defaultJob$ = new BehaviorSubject<DefaultMaterialJob|null>(null);

  readonly defaultTask$ = new BehaviorSubject<DefaultMaterialTask|null>(null);

  readonly initialValue$ = new BehaviorSubject<MaterialLineData[]>([]);

  readonly _subscriptions: Subscription[] = [];

  /**
   * If the material list is invalid, meaning
   * the user has not selected a product or inputted
   * any name if its a once off.
   *
   * @return {boolean}
   */
  get bIsMaterialsInValid(): boolean {
    return this.arMaterialLineItem.findIndex(item => {
      return item.type == 'once_off_purchase' ? blank(item.notes) : blank(item.product.value);
    }) > -1;
  }

  /**
   * get the deleted line items
   */
  get deletedLineItems(): LooseObject {
    return this.arDeletedLineItems.filter(lineItems => {
      if (!isUndefined(lineItems) || !isEmpty(lineItems)) {
        if (!isEmpty(lineItems.id)) {
          return true;
        }
      }
      return false;
    });
  }

  constructor(
    public objDialog: MatDialogRef<AddMaterialComponent>,
    @Inject(MAT_DIALOG_DATA) public objJobData: Job,
    private record: RecordService,
    private notificationService: NotificationService,
    private readonly _api: ApiService,
  ) {}

  /// see: OnInit::ngOnInit
  ngOnInit(): void {
    if (filled(get(this.objJobData, 'pricebook_id'))) {
      this.defaultPricebook$.next({
        id: get(this.objJobData, 'pricebook_id'),
        text: get(this.objJobData, 'pricebook_text'),
      });
    }

    this._subscriptions.push(
      observe({
        before: () => this.fetching$.next(true),
        after: () => this.fetching$.next(false),
        observable: () => this.record.getRecord('jobs', this.objJobData.id).pipe(
          switchMap((data) => {
            if (data_is_true(get(data, 'record_details.enable_cost_centers'))) {
              return this._api.callPrivate$({
                path: '/jobs/tasks',
                body: {
                  'job_id': get(data, 'record_details.id'),
                },
                expectsNewFormat: true,
                onSuccessResponse: (response) => [data, response.get('items')],
              });
            }

            return of([data, []]);
          })
        ),
      }).subscribe(([response, tasks]) => {
        /// set default job
        if (filled(get(response, 'record_details.id'))) {
          this.defaultJob$.next({
            id: get(response, 'record_details.id'),
            enable_cost_centers: data_is_true(get(response, 'record_details.enable_cost_centers')),
          });
        }

        if (data_is_false(get(response, 'record_details.enable_cost_centers'))) {
          const activity = arr_first(_filter(
            get(response , 'activities.data', []), (activity) => get(activity, 'activity_type') == 'task'
          ));

          // FC-4212: If job has only 1 task, default the task selection to that one task.
          if (filled(activity)) {
            this.defaultTask$.next({
              id: get(activity, 'id'),
              name: get(activity, 'activity_name'),
            });
          }
        } else if (arr_count(tasks) === 1) {
          const task = arr_first(tasks);

          this.defaultTask$.next({
            id: get(task, 'id'),
            name: get(task, 'name'),
          });
        }

        if (filled(get(response, 'related_data.materials', []))) {
          this.initialValue$.next(_map(
            get(response, 'related_data.materials', []),
            (line) => ({
              id: line.id,
              task_id: line.activity_id,
              task_name: line.activity_name,
              quantity: line.quantity,
              unit_cost: line.unit_cost || 0,
              unit_price: line.unit_price || 0,
              item_id: line.item_id,
              item_name: line.product_name,
              item_code: either(line, ['code', 'item_code']),
              description: line.notes,
              once_off_product_name: line.product,
              markup: toFormattedNumber(line.markup),
              customer_invoice_id: line.customer_invoice_id,
              customer_invoice_number: line.invoice_number,
              supplier_pricing: get(line, 'supplier_pricing', []),
              related_products: get(line, 'related_products', []),
            })
          ));
        }
      })
    );
  }

  /// see: OnDestroy::ngOnDestroy
  ngOnDestroy(): void {
    this._subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  /**
   * Send the materials to the API,
   * only send those who have changes.
   *
   * @returns {void}
   */
  logMaterials(): void {
    if (!isEmpty(this.arMaterialLineItem) || !isEmpty(this.deletedLineItems)) {
      this.bIsTouched = true;
      // notify the user that we will permanently delete the materials when there is removed line item
      if (!isEmpty(this.deletedLineItems)) {
        this.notificationService.sendConfirmation('confirm_delete_materials').pipe(
          filter(confirmation => confirmation.answer === true),
        ).subscribe(() => {
          this.saveMaterials()
        })
      } else {
        this.saveMaterials()
      }
    } else {
      this.notificationService.notifyWarning('required_materials')
    }
  }

  /**
   * save materials
   *
   * @returns
   */
  saveMaterials(): void {
    if (!this.bIsMaterialsInValid) {

      this.bIsLoading = true;

      let arMaterialsToSave = this.arMaterialLineItem.map((item, index) => {
        let itemData = item.getMaterialData(this.objJobData.id);
        itemData.sort_seq = index;
        return itemData;
      });

      // if the user removed existing line item, also need to dlete the materials record
      forkJoin([
        iif(() => !isEmpty(arMaterialsToSave),
          this.record.saveMultipleRecord('materials', arMaterialsToSave),
          of({})
        ),
        iif(() => !isEmpty(this.deletedLineItems),
          this.record.deleteMultipleRecord('materials', this.deletedLineItems.map( lineItems => {
            return { id: lineItems.id }
          } )),
          of({})
        )
      ]).subscribe(() => {
        this.bIsTouched = false;
        this.bIsLoading = false;
        this.objDialog.close('save');
      });
    } else {
       this.notificationService.notifyError('please_complete_the_form');
    }
  }

  onUpdatedMaterials(event: OnMaterialsUpdate): void {
    if (event.mode == 'remove') {
      this.arDeletedLineItems.push(event.material);
    } else {
      this.arMaterialLineItem = event.materials;
    }
  }
}
